import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, catchError, from, mergeMap, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OnlineStatusService } from '../../osp-ui/services/online-status.service';
import { HttpStatusCode } from '../../shared/enums/http-status-codes.enum';
import { ToastService } from '../../shared/services/toast.service';
import { AuthRealmService } from '../services/auth-realm.service';
import { AuthService } from '../services/auth.service';
import { ErrorTrackerService } from '../services/error-tracker/error-tracker.service';
import { LanguageService } from '../services/language.service';
import { RequestErrorService } from '../services/request-error.service';
import { UrlErrorService } from '../services/url-error.service';
import { UserConfigurationService } from '../services/user-configuration.service';
import { SKIP_GLOBAL_ERROR_HANDLER } from '../tokens/http-context-tokens';
import { InterceptorResult } from './contract/http-interceptor-result.contract';

const unfinishedTaskUrl = '/tasks/unfinished/';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private readonly dimensionSystemHeader = 'Accept-Dimension-Unit';
  private readonly retryHeader = 'X-Auth-Retry';

  private readonly authService = inject(AuthService);
  private readonly languageService = inject(LanguageService);
  private readonly reqErrorService = inject(RequestErrorService);
  private readonly urlErrorService = inject(UrlErrorService);
  private readonly toastService = inject(ToastService);
  private readonly onlineStatusService = inject(OnlineStatusService);
  private readonly userConfigurationService = inject(UserConfigurationService);
  private readonly errorTrackerService = inject(ErrorTrackerService);

  private refreshInFlight: Promise<string> | null = null;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): InterceptorResult<unknown> {
    req = req.clone({ url: AuthRealmService.prepareUrl(req.url) });

    if (!req.headers.has('Content-Type')) {
      req = req.clone({
        headers: req.headers.set('Content-Type', 'application/json'),
      });
    }

    if (!req.headers.has('X-Realm') && !req.url.includes(environment.APP_TRANSLATION_BASE_URL)) {
      req = req.clone({
        headers: req.headers.set('X-Realm', AuthRealmService.getRealm()),
      });
    }

    if (
      this.languageService.getCurrentLocale() &&
      !req.headers.has('Accept-Language') &&
      !req.url.includes(environment.APP_TRANSLATION_BASE_URL)
    ) {
      req = req.clone({
        headers: req.headers.set('Accept-Language', this.languageService.getCurrentLocale()),
      });
    }

    if (
      !req.headers.has('Time-Zone-ID') &&
      Intl.DateTimeFormat().resolvedOptions().timeZone &&
      !req.url.includes(environment.APP_TRANSLATION_BASE_URL)
    ) {
      req = req.clone({
        headers: req.headers.set('Time-Zone-ID', Intl.DateTimeFormat().resolvedOptions().timeZone),
      });
    }

    req = this.assignHeaderDimensionUnit(req);

    const tokenPromise = this.ensureToken();

    if (!req.headers.has('Authorization')) {
      tokenPromise.then((token) => {
        req = req.clone({
          headers: req.headers.set('Authorization', 'Bearer ' + token),
        });
      });
    }

    return from(tokenPromise).pipe(
      mergeMap(() =>
        next.handle(req).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === HttpStatusCode.NOT_FOUND && req.url.includes(unfinishedTaskUrl)) {
              return throwError(() => error);
            }

            if (
              (error.status === HttpStatusCode.UNAUTHORIZED || error.status === HttpStatusCode.FORBIDDEN) &&
              !req.headers.has(this.retryHeader)
            ) {
              // Attempt a single refresh and retry (force a token refresh attempt)
              return from(this.ensureToken(true)).pipe(
                mergeMap((newToken) => {
                  const retryReq = req.clone({
                    setHeaders: { Authorization: `Bearer ${newToken}`, [this.retryHeader]: '1' },
                  });
                  return next.handle(retryReq);
                }),
                catchError(() => {
                  try {
                    this.authService.logout(); // On refresh failure, logout
                  } catch {
                    // swallow error
                  }
                  return EMPTY;
                }),
              );
            }

            return this.handleError(error, req);
          }),
        ),
      ),
    );
  }

  private assignHeaderDimensionUnit(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const dimensionSystem = this.userConfigurationService?.getUserDimensionSystem();
    return !req.headers.has(this.dimensionSystemHeader) && dimensionSystem
      ? req.clone({ headers: req.headers.set(this.dimensionSystemHeader, dimensionSystem) })
      : req;
  }

  private handleError = (error: HttpErrorResponse, req: HttpRequest<unknown>) => {
    return of(error).pipe(
      mergeMap(() => this.handleCompleteStandaloneTaskError(error, req)),
      mergeMap(() => this.handleErrorExternally(error, req)),
      mergeMap(() => this.handleErrorInternally(error, req)),
    );
  };

  private handleCompleteStandaloneTaskError(
    error: HttpErrorResponse,
    req: HttpRequest<unknown>,
  ): Observable<never | HttpErrorResponse> {
    return this.reqErrorService.handleError(error, req);
  }

  private handleErrorExternally(
    error: HttpErrorResponse,
    req: HttpRequest<unknown>,
  ): Observable<never | HttpErrorResponse> {
    return this.reqErrorService.handleError(error, req);
  }

  private handleErrorInternally(error: HttpErrorResponse, req: HttpRequest<unknown>): Observable<never> {
    let message = this.translate('shared.requestInterceptorService.techError');
    let statusCode: HttpStatusCode = HttpStatusCode.UNKNOWN_ERROR;

    if (this.isHttpErrorResponse(error)) {
      statusCode = error.status;
      switch (error.status) {
        case HttpStatusCode.BAD_REQUEST:
          message = this.translate('shared.requestInterceptorService.techErrorInvalidRequest');
          break;
        case HttpStatusCode.UNAUTHORIZED:
        case HttpStatusCode.FORBIDDEN:
          message = this.translate('shared.requestInterceptorService.techErrorInvalidNoAuthorization');
          break;
      }
    }

    if (
      !this.urlErrorService.isExpectedOfflineOrAllowedErrorUrl(
        this.onlineStatusService.onlineStatus,
        req.url,
        error.status,
      )
    ) {
      const sentryId = this.translate('shared.requestInterceptorService.sentryNotAvailable');
      this.errorTrackerService.captureMessage({
        message: JSON.stringify(error),
        captureContext: (scope) => {
          return scope
            .setLevel('error')
            .setTag('http_method', req.method)
            .setTag('error_code', error.status)
            .setExtra('payload', JSON.stringify(req.body))
            .setExtra('http_method', req.method)
            .setExtra('error_code', error.status)
            .setExtra('response', JSON.stringify(error.error));
        },
      });

      const shouldSkipErrorHandler = req.context.get(SKIP_GLOBAL_ERROR_HANDLER);

      if (
        !this.urlErrorService.isThumbnailUrl(req.url) &&
        !shouldSkipErrorHandler &&
        statusCode !== HttpStatusCode.UNKNOWN_ERROR
      ) {
        this.toastService.showError({ summary: message + '\n' + `${statusCode.valueOf()} - ID: ${sentryId}` });
      }

      return throwError(() => error);
    }

    if (this.urlErrorService.shouldThrowObservable(req.url, error.status)) {
      return throwError(() => error);
    }

    return EMPTY;
  }

  private isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse;
  }

  private translate(key: string): string {
    return this.languageService.getInstant(key);
  }

  private ensureToken(forceRefresh = false): Promise<string> {
    if (!forceRefresh && !this.authService.isTokenExpired()) {
      return Promise.resolve(this.authService.token);
    }

    this.refreshInFlight ??= this.authService.updateToken(0).finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }
}
